import React, {FC, ReactNode, useMemo, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CustomError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {isTeamNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {
  InterfaceEventsEnum,
  SettingsTagTypesEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {SimpleDropdownItemWithColorAndLoader} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItemWithColorAndLoader';
import {getColorHighlight, getEmojiHighlight} from '../../../../helpers/models/highlightHelpers';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {TagModel} from '../../../../models/api/tagModel';
import {TagTemplate} from '../../tags/helpers';
import {ArchiveTagWithDialog} from './archiveTagWithDialog';
import {DeleteTagWithDialog} from './deleteTagWithDialog';
import {TagEditorBodyInitialState} from './tagEditorBody';
import {TagEditorDropdown} from './tagEditorDropdown';

/*
 * Props.
 */

interface EditTagDropdownProps extends DropdownProps {
  userId: number;
  teammates: ReadonlyArray<TeammateModel>;
  tag: TagModel;
  team?: TeamModel;
  parentTag?: TagModel;
  updateTagAsync: (template: TagTemplate) => Promise<void>;
  isVisibleCustomView: boolean;
  backButtonLabel?: ReactNode;
  onBackButtonClick: () => void;
  hasPermission: (namespace: string, permission: PermissionsEnum) => boolean;
  hasPreventPinTeamTags: boolean;
  canCreateCompanyTags: boolean;
}

/*
 * Component.
 */

export const EditTagDropdown: FC<EditTagDropdownProps> = (props) => {
  const {tag, parentTag, isVisibleCustomView} = props;
  const [isSaving, setIsSaving] = useState(false);
  const [tagError, setTagError] = useState<CustomError>();

  const initialState = useMemo<TagEditorBodyInitialState>(() => {
    const highlightEmoji = getEmojiHighlight(tag);
    const highlightColor = getColorHighlight(tag);

    return {
      name: tag.name,
      description: tag.description,
      namespace: tag.namespace,
      parentTags: (parentTag && [parentTag]) || [],
      highlightEmoji,
      highlightColor,
      showInWorkspaces: isVisibleCustomView,
      showInCells: Boolean(tag.showInCells),
    };
  }, [tag, parentTag, isVisibleCustomView]);

  /*
   * Handlers.
   */

  const onArchive = () => {
    trackInterfaceEvent(InterfaceEventsEnum.TAG_EDIT_TAG_ARCHIVE, {
      tag_type: isTeamNamespace(tag.namespace) ? SettingsTagTypesEnum.TEAM : SettingsTagTypesEnum.INDIVIDUAL,
      namespace: tag.namespace,
      tag_id: tag.id,
    });
    if (props.onRequestClose) {
      props.onRequestClose();
    }
  };

  const onDelete = () => {
    trackInterfaceEvent(InterfaceEventsEnum.TAG_EDIT_TAG_DELETE, {
      tag_type: isTeamNamespace(tag.namespace) ? SettingsTagTypesEnum.TEAM : SettingsTagTypesEnum.INDIVIDUAL,
      namespace: tag.namespace,
      tag_id: tag.id,
    });
    if (props.onRequestClose) {
      props.onRequestClose();
    }
  };

  const onUpdateTag = async (tagTemplate: TagTemplate) => {
    setIsSaving(true);
    setTagError(undefined);

    try {
      await props.updateTagAsync(tagTemplate);

      if (props.onRequestClose) {
        props.onRequestClose();
      }
    } catch (error) {
      if (!(error instanceof CustomError)) {
        throw error;
      }

      // In case of conflict with an existing tag, display an error.
      setIsSaving(false);
      setTagError(error);
    }
  };

  const renderDropdownItems = (onRequestClose: () => void) => (
    <>
      <DeleteTagWithDialog
        namespace={tag.namespace}
        tagsToActOn={[tag]}
        onDelete={onDelete}
        onClose={onRequestClose}
        isInArchivedView={false}
      >
        {(isLoading, onDeleteTag) => (
          <SimpleDropdownItemWithColorAndLoader
            onSelect={(event) => {
              event.preventDefault();
              onDeleteTag();
            }}
            color={PaletteColorsEnum.RED}
            isLoading={isLoading}
          >
            <Trans id="tag-edit-delete">Delete</Trans>
          </SimpleDropdownItemWithColorAndLoader>
        )}
      </DeleteTagWithDialog>
      <ArchiveTagWithDialog
        namespace={tag.namespace}
        tagsToActOn={[tag]}
        onArchive={onArchive}
        onClose={onRequestClose}
      >
        {(isLoading, onArchiveTag) => (
          <SimpleDropdownItemWithColorAndLoader
            onSelect={(event) => {
              event.preventDefault();
              onArchiveTag();
            }}
            isLoading={isLoading}
          >
            <Trans id="tag-edit-archive">Archive</Trans>
          </SimpleDropdownItemWithColorAndLoader>
        )}
      </ArchiveTagWithDialog>
    </>
  );

  /*
   * Render.
   */

  return (
    <TagEditorDropdown
      {...props}
      initialState={initialState}
      title={<Trans id="tag-edit-title">Edit Tag</Trans>}
      okButtonLabel={<Trans id="tag-edit-ok">Save</Trans>}
      onOkClick={onUpdateTag}
      renderExtraDropdownItems={renderDropdownItems}
      isSystemTag={tag.isSystemTag}
      isEditing
      isSaving={isSaving}
      tagError={tagError}
    />
  );
};
